import { Button } from "@mui/material";
import { RootState } from "@store/store";
import { useSelector } from "react-redux";
import { BofuTabCode } from "@interfaces/response";
import { useCallback, useEffect, useState } from "react";
import useBofuTabs from "@hooks/useBofuTabs";
import { useNavigate } from "react-router-dom";
import { usePostValidateNextStageMutation } from "@api/applications";
import { STATUS_CATALOG, TAX_SYSTEM_TYPE } from "@helpers/constantes";
import * as Sentry from "@sentry/react";

interface ButtonContinueProps {
  tabCode: BofuTabCode;
  isFirstTab?: boolean;
  onClick?: () => void;
}

const ButtonContinue = (props: ButtonContinueProps) => {
  const navigate = useNavigate();
  const { currentTab, nextTab, isLastTab } = useBofuTabs(props.tabCode);

  const bofuTabs = useSelector((state: RootState) => state.bofu.bofu_tabs_obj);
  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);

  const [triggerPostNextStage] = usePostValidateNextStageMutation();

  const [isDisabled, setIsDisabled] = useState<boolean>(false);

  const runValidations = useCallback(() => {
    if (isLastTab) {
      if (currentTab?.tab_status === "incomplete" || currentTab?.tab_status === undefined) {
        return false;
      }
    }

    if (nextTab) {
      if (nextTab.is_disabled) {
        return false;
      }

      if (currentTab?.tab_status === "incomplete" || currentTab?.tab_status === undefined) {
        return false;
      }
    }

    return true;
  }, [isLastTab, currentTab, nextTab]);

  const handleClick = async () => {
    try {
      const canContinue = runValidations();

      if (canContinue) {
        if (isLastTab) {
          let next_status = "";
          let next_url = "";

          if (application.tax_systems?.tax_system_type === TAX_SYSTEM_TYPE.PFAE.code) {
            next_status = STATUS_CATALOG.validando_expediente_pfae.code;
            next_url = STATUS_CATALOG.validando_expediente_pfae.url;
          } else {
            next_status = STATUS_CATALOG.validando_expediente_pm.code;
            next_url = STATUS_CATALOG.validando_expediente_pm.url;
          }

          await triggerPostNextStage({
            applicationId: application.id,
            statusCode: next_status,
            username: `BOFU - ${accountUser.email}`,
          }).unwrap();

          navigate(next_url);
        } else if (props.isFirstTab) {
          const { dictamen_juridico } = STATUS_CATALOG;

          await triggerPostNextStage({
            applicationId: application.id,
            statusCode: dictamen_juridico.code,
            username: `BOFU - ${accountUser.email}`,
          }).unwrap();

          navigate(dictamen_juridico.url);
        } else {
          navigate(nextTab?.url || "");
        }
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          component: "TabBofu-sendToDictamenJuridico",
        },
      });
    }
  };

  useEffect(() => {
    const canContinue = runValidations();
    setIsDisabled(!canContinue);
  }, [bofuTabs, runValidations]);

  return (
    <Button onClick={handleClick} variant="default" type="button" disabled={isDisabled}>
      Continuar
    </Button>
  );
};

export default ButtonContinue;
